/**
 * Allys Property Card
 * 
 * Theme-specific wrapper for the core PropertyCard component.
 * Customizes the appearance with Allys-specific styling.
 * Supports featured content badges for prominently featured properties.
 */

'use client';

import { PropertyCard as CorePropertyCard } from '@/components/properties';
import type { Property } from '@/lib/api/clients/properties';
import { DEFAULT_CURRENCY } from '@/lib/config/app.config';
import type { PropertyCardProps as ThemePropertyCardProps } from '@routeroyal/theme-core';
import type { FeaturedPlacementSummary, FeaturedBadgeVariant } from '@/lib/api/clients/featured-content';
import { getPrimaryFeaturedPlacement } from '@/lib/api/clients/featured-content';
import { FeaturedBadge } from '@/components/listings/FeaturedBadge';

/**
 * Map theme-core property format to Route Royal API Property format
 */
function mapThemePropertyToApiProperty(themeProperty: ThemePropertyCardProps['property']): Property {
  // Extract numeric price from string (remove currency symbols, commas, etc.)
  const priceMatch = themeProperty.price?.match(/[\d,.]+/);
  const priceValue = priceMatch ? parseFloat(priceMatch[0].replace(/,/g, '')) : 0;

  return {
    id: themeProperty.id,
    name: themeProperty.name,
    description: themeProperty.address,
    status: mapStatusToApiStatus(themeProperty.status),
    organisationId: 'default-org', // Required field
    locationId: 'default-location', // Required field
    propertyTypeId: 'default-type', // Required field
    managedAssetId: 'default-asset', // Required field
    reference: null,
    currentOwnerUserId: null,
    primaryAgentId: null,
    bedrooms: themeProperty.bedrooms ?? null,
    bathrooms: themeProperty.bathrooms ?? null,
    loungeRoomCount: null,
    toiletCount: null,
    garageCount: null,
    carportCount: null,
    openParkingCount: null,
    parkingSpaces: null,
    storeys: null,
    floorNumber: null,
    totalFloorArea: themeProperty.area ? parseFloat(themeProperty.area) : null,
    totalFloorAreaUnit: 'SQM',
    landArea: null,
    landAreaUnit: null,
    yearBuilt: null,
    yearRenovated: null,
    condition: null,
    aspect: null,
    viewType: null,
    titleType: null,
    zoningCategory: null,
    conservationArea: false,
    primaryCertificationRating: null,
    epcExpiryDate: null,
    currency: DEFAULT_CURRENCY,
    heroMedia: themeProperty.image ? {
      id: themeProperty.id + '-hero',
      url: themeProperty.image,
      thumbnailUrl: themeProperty.image,
      publicId: themeProperty.id + '-hero-public',
      filename: 'hero.jpg',
      contentType: 'image/jpeg',
      size: 0,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } : null,
    primaryImageUrl: themeProperty.image,
    mediaLinks: [],
    attributeValues: {
      askingPrice: {
        attributeId: 'asking-price',
        attributeCode: 'ASKING_PRICE',
        attributeName: 'Asking Price',
        valueType: 'NUMBER',
        valueNumber: priceValue,
        valueString: themeProperty.price,
      },
    },
    createdById: 'system',
    updatedById: 'system',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  } as Property;
}

/**
 * Map theme status to API status
 */
function mapStatusToApiStatus(status?: 'available' | 'sold' | 'reserved'): 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED' | 'DELETED' {
  switch (status) {
    case 'sold':
      return 'ARCHIVED';
    case 'reserved':
      return 'INACTIVE';
    case 'available':
    default:
      return 'ACTIVE';
  }
}

/**
 * Extended props for Allys Property Card with featured content support
 */
export interface AllysPropertyCardProps extends ThemePropertyCardProps {
  locale?: string;
  /** Featured placements for this property */
  featuredPlacements?: FeaturedPlacementSummary[];
  /** Whether to show the featured badge */
  showFeaturedBadge?: boolean;
}

/**
 * Allys Property Card Component
 * 
 * Wraps CorePropertyCard with Allys theme styling.
 * Supports featured content badges for prominently featured properties.
 */
export function AllysPropertyCard({ 
  property, 
  variant = 'default', 
  locale = 'en',
  featuredPlacements = [],
  showFeaturedBadge = true,
}: AllysPropertyCardProps) {
  // Return null if no property is provided
  if (!property) {
    return null;
  }

  // Map theme property format to API property format
  const apiProperty = mapThemePropertyToApiProperty(property);
  
  // Get primary featured badge
  const primaryFeatured = showFeaturedBadge && featuredPlacements.length > 0
    ? getPrimaryFeaturedPlacement(featuredPlacements)
    : null;

  return (
    <div className="group block relative">
      {/* Featured Badge - positioned top-right */}
      {primaryFeatured?.badgeVariant && (
        <div className="absolute top-3 right-3 z-10">
          <FeaturedBadge
            variant={primaryFeatured.badgeVariant as FeaturedBadgeVariant}
            label={primaryFeatured.label}
            size="sm"
          />
        </div>
      )}
      
      <CorePropertyCard
        property={apiProperty}
        variant={variant}
        href={`/${locale}/properties/${property.id}`}
        className="
          bg-[var(--color-surface)] rounded-lg overflow-hidden 
          hover:shadow-md transition-all duration-300 
          border border-[var(--color-border)] hover:border-[var(--color-primary)]/20
          transform hover:-translate-y-0.5
        "
      />
    </div>
  );
}
